import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  clearAuthToken,
  fetchCurrentUser,
  getAuthToken,
  loginUser,
  setAuthToken,
  signupUser
} from './apiClient';
import type { User } from '../types';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  login: (payload: { email: string; password: string }) => Promise<void>;
  signup: (payload: { name: string; email: string; password: string }) => Promise<void>;
  refreshUser: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const STORAGE_KEY = 'inspectai-token';

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (stored) {
      setAuthToken(stored);
      setToken(stored);
      fetchCurrentUser()
        .then((currentUser) => {
          setUser(currentUser);
        })
        .catch(() => {
          window.localStorage.removeItem(STORAGE_KEY);
          clearAuthToken();
        })
        .finally(() => setLoading(false));
      return;
    }
    setLoading(false);
  }, []);

  const handleAuthSuccess = useCallback(
    async (authToken: string, authUser: User) => {
      setAuthToken(authToken);
      setToken(authToken);
      setUser(authUser);
      setError(null);
      window.localStorage.setItem(STORAGE_KEY, authToken);
      await queryClient.invalidateQueries();
    },
    [queryClient]
  );

  const login = useCallback(
    async (payload: { email: string; password: string }) => {
      try {
        setLoading(true);
        setError(null);
        const { token: authToken, user: authUser } = await loginUser(payload);
        await handleAuthSuccess(authToken, authUser);
        navigate('/app', { replace: true });
      } catch (err) {
        console.error(err);
        setError('Unable to log in with those credentials.');
        clearAuthToken();
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    },
    [handleAuthSuccess, navigate]
  );

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const currentUser = await fetchCurrentUser();
      setUser(currentUser);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  const signup = useCallback(
    async (payload: { name: string; email: string; password: string }) => {
      try {
        setLoading(true);
        setError(null);
        const { token: authToken, user: authUser } = await signupUser(payload);
        await handleAuthSuccess(authToken, authUser);
        navigate('/app', { replace: true });
      } catch (err) {
        console.error(err);
        setError('Unable to create account. Please try a different email.');
        clearAuthToken();
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    },
    [handleAuthSuccess, navigate]
  );

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    clearAuthToken();
    window.localStorage.removeItem(STORAGE_KEY);
    queryClient.clear();
    navigate('/', { replace: true });
  }, [navigate, queryClient]);

  useEffect(() => {
    const tokenFromClient = getAuthToken();
    if (!token && tokenFromClient) {
      setToken(tokenFromClient);
    }
  }, [token]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, loading, error, login, signup, refreshUser, logout }),
    [user, token, loading, error, login, signup, refreshUser, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
